<template>
  <div class="bg-black h-full flex flex-col">
    <header
      class="
        flex
        items-center
        justify-between
        px-6
        py-4
        text-white
        bg-header-grey
        border-b border-strong-grey
      "
    >
      <div>
        <h1 class="text-lg font-semibold">{{ session.title }}</h1>
        <p class="text-xs text-gray-300" v-if="session.details.room_location">
          {{ session.details.room_location }}
        </p>
      </div>
      <ViceButton data-cy="leave-session" mediumRed class="rounded-full" @click="leaveSession">
        <PhoneIcon
          class="w-6 h-5 transform"
          style="--tw-rotate: 45deg; --tw-scale-y: -1; --tw-translate-y: 2px"
        />
        Leave session
      </ViceButton>
    </header>

    <div class="bg-black flex flex-1 min-h-0">
      <main
        v-if="sessionStore.isSessionEnded"
        class="flex px-10 md:px-0 md:w-full bg-black justify-center items-center overflow-hidden"
      >
        <SessionEnd :isClinician="false" :sessionId="$page.props.session.unique_id" />
      </main>

      <main
        v-else-if="showNetworkChangeWarning"
        class="flex px-10 md:px-0 md:w-full bg-black justify-center items-center overflow-hidden"
      >
        <NetworkChangeReload :countdown="reloadCountdown" />
      </main>

      <template v-else>
        <!-- There's nothing to watch on mobile - only show the video placeholder on desktop -->
        <div class="hidden md:flex flex-grow items-center justify-center text-center px-6">
          <p class="text-gray-400 prose-lg">
            You're participating offline. Use the chat and reflection panel on the right.
          </p>
        </div>

        <!-- Sidebar (matches the online session's SessionSidebar layout) - fills the screen on mobile -->
        <div
          class="
            bg-vice-light
            relative
            overflow-hidden`
            flex-1
            md:flex-none
            md:my-2
            md:mx-2
            md:rounded-2xl
            md:w-1/3
            md:min-w-sidebar
          "
        >
          <div class="absolute w-full flex flex-col h-full">
            <nav class="relative z-0 flex w-full">
              <button
                data-cy="offline-chat-tab"
                @click="activeTab = 'chatTab'"
                class="
                  text-gray-500
                  group
                  relative
                  flex-1
                  justify-center
                  flex
                  overflow-hidden
                  py-4
                  px-2
                  text-center
                "
                :class="{ 'text-vice-teal-900': activeTab === 'chatTab' }"
              >
                <div class="flex justify-center items-center relative">
                  <ChatAlt2Icon class="h-5 w-5 mr-1" />
                  <span
                    class="text-xxs font-light text-gray-500"
                    :class="{ 'text-gray-900 font-bold': activeTab === 'chatTab' }"
                  >
                    CHAT
                  </span>
                </div>
                <span
                  aria-hidden="true"
                  class="absolute inset-x-0 bottom-0 h-0.5"
                  :class="activeTab === 'chatTab' ? 'bg-vice-teal-400' : 'bg-gray-300'"
                ></span>
              </button>
              <button
                data-cy="offline-reflection-tab"
                @click="activeTab = 'reflectionTab'"
                class="
                  text-gray-500
                  group
                  relative
                  flex-1
                  justify-center
                  flex
                  overflow-hidden
                  py-4
                  px-2
                  text-center
                  border-l
                "
                :class="{ 'text-vice-teal-900': activeTab === 'reflectionTab' }"
              >
                <div class="flex justify-center items-center">
                  <SessionInfoIcon class="h-4 w-4 mr-1" />
                  <span
                    class="text-xxs font-light text-gray-500"
                    :class="{ 'text-gray-900 font-bold': activeTab === 'reflectionTab' }"
                  >
                    REFLECTION
                  </span>
                </div>
                <span
                  aria-hidden="true"
                  class="absolute inset-x-0 bottom-0 h-0.5"
                  :class="activeTab === 'reflectionTab' ? 'bg-vice-teal-400' : 'bg-gray-300'"
                ></span>
              </button>
            </nav>

            <ChatLayout
              v-show="activeTab === 'chatTab'"
              :clinicianName="clinicianName"
              :participants-list="[]"
              :userIsObserver="sessionStore.userIsObserver"
            />

            <ReflectionLayout v-show="activeTab === 'reflectionTab'" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { ChatAlt2Icon, PhoneIcon } from '@heroicons/vue/solid';
import JitsiService from '@/Services/JitsiService';
import useJitsiCommands from '@/Composables/useJitsiCommands';
import useSessionHandlers from '@/Composables/useSessionHandlers';
import { useSetupSessionData } from '@/Composables/useSetupSessionData';
import useSendDeviceMessage from '@/Composables/useSendDeviceMessage';
import { SESSION_CALL_HELPERS, DISPLAY_NAME } from '@/Utils/vice-constants';
import ChatLayout from '@/Components/Chat/ChatLayout.vue';
import ReflectionLayout from '@/Components/Session/Sidebar/ReflectionLayout.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import SessionInfoIcon from '@/Components/Icons/SessionInfoIcon.vue';
import SessionEnd from '@/Components/Modals/SessionEnd.vue';
import NetworkChangeReload from '@/Components/Modals/NetworkChangeReload.vue';

export default {
  name: 'SessionCallOffline',
  components: {
    SessionEnd,
    NetworkChangeReload,
    ChatLayout,
    ReflectionLayout,
    ViceButton,
    SessionInfoIcon,
    ChatAlt2Icon,
    PhoneIcon,
  },
  props: {
    session: Object,
  },
  setup(props) {
    const jitsiRoom = ref(null);
    const disconnectFn = ref(null);
    const activeTab = ref('chatTab');
    const isConnected = ref(false);
    const isLeaving = ref(false);
    const isReconnecting = ref(false);

    const sendJitsiCommand = useJitsiCommands(jitsiRoom);
    const { sessionStore } = useSetupSessionData();
    const { commands, helpers } = useSessionHandlers();
    const sessionId = props.session.unique_id.replace(/-/g, '');
    const jitsiService = JitsiService();

    sessionStore.jitsiCommands = sendJitsiCommand;
    sessionStore.deviceMessageSender = useSendDeviceMessage();

    const clinicianName = props.session.details.clinician?.name || '';

    const disconnectJitsi = async () => {
      try {
        await disconnectFn.value?.();
      } catch (error) {
        console.error(error);
      }
    };

    // Mobile browsers silently kill the Jitsi websocket while the screen is locked/backgrounded.
    // Reconnect and re-sync chat history whenever that happens, instead of leaving the
    // participant stuck until they reload the page.
    //
    // JitsiService keeps its connection/room state in module-level variables shared across every
    // call, so two connect/disconnect calls in flight at once can stomp on each other's state
    // (e.g. one attempt's teardown tearing down the connection the other just established, or
    // one's `disconnect()` marking a later, unrelated drop as "requested" and suppressing its
    // reconnect). That's why the library's own unexpected-disconnect callback below reconnects
    // via `reconnectJitsi()` rather than calling `connectToJitsi()` directly - every trigger
    // (this callback, visibilitychange) funnels through the same `isReconnecting`-guarded
    // function so only one reconnect is ever in flight.
    const connectToJitsi = () =>
      jitsiService
        .makeConnection(sessionId, {}, commands, () => {
          isConnected.value = false;
          reconnectJitsi();
        })
        .then((jitsiInstance) => {
          jitsiRoom.value = jitsiInstance.room;
          disconnectFn.value = jitsiInstance.disconnect;
          isConnected.value = true;
          // Reserved display name the clinician/participant video-grid handlers
          // (useGroupCallHandlers/useConsultationHandlers) already filter out of
          // remote streams - keeps this silent chat-only participant off-camera.
          jitsiRoom.value.setDisplayName(DISPLAY_NAME.OBSERVER);
          jitsiRoom.value.join();

          // Backfill any messages sent while the connection was down.
          helpers[SESSION_CALL_HELPERS.INITIALIZE_CHAT_MESSAGES]();
        })
        .catch((error) => {
          console.error('error connecting to jitsi: ', error);
          isConnected.value = false;
          if (!isLeaving.value && !sessionStore.isSessionEnded) {
            setTimeout(connectToJitsi, 3000);
          }
        });

    const reconnectJitsi = () => {
      if (isLeaving.value || isReconnecting.value || sessionStore.isSessionEnded) {
        return;
      }

      isReconnecting.value = true;
      disconnectJitsi()
        .then(() => {
          isConnected.value = false;
          return connectToJitsi();
        })
        .finally(() => {
          isReconnecting.value = false;
        });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        reconnectJitsi();
      }
    };

    // Reconnecting the Jitsi connection in place after the underlying network interface changes
    // (wifi <-> mobile data, or switching wifi networks) has proven unreliable - the connection
    // can come back in a state where chat never recovers. A full page reload is what already
    // reliably fixes it for participants today, so trigger that directly instead, with a brief
    // countdown so it doesn't look like the page just froze.
    const RELOAD_COUNTDOWN_SECONDS = 3;
    const showNetworkChangeWarning = ref(false);
    const reloadCountdown = ref(RELOAD_COUNTDOWN_SECONDS);
    const reloadCountdownInterval = ref(null);

    const handleNetworkChange = () => {
      if (isLeaving.value || showNetworkChangeWarning.value) {
        return;
      }

      showNetworkChangeWarning.value = true;
      reloadCountdown.value = RELOAD_COUNTDOWN_SECONDS;

      reloadCountdownInterval.value = setInterval(() => {
        reloadCountdown.value -= 1;
        if (reloadCountdown.value <= 0) {
          clearInterval(reloadCountdownInterval.value);
          window.location.reload();
        }
      }, 1000);
    };

    // Join the same presence channel the clinician's sidebar listens on, so this offline
    // participant shows up there (merged into the Observers list) instead of being invisible.
    const presenceChannelName = `presence.channel.${props.session.unique_id}`;

    onMounted(() => {
      document.title = props.session.title;

      connectToJitsi();
      helpers[SESSION_CALL_HELPERS.INITIALIZE_CHAT_MESSAGES]();

      // Handle socket connection after log-in
      const socket = window.Echo.connector.socket;
      socket.once('connect', () => window.Echo.join(presenceChannelName));
      socket.disconnect();
      socket.connect();

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('online', handleNetworkChange);
    });

    onUnmounted(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleNetworkChange);
      clearInterval(reloadCountdownInterval.value);
      window.Echo.leaveChannel(`presence-${presenceChannelName}`);
      disconnectJitsi();
    });

    const leaveSession = async () => {
      isLeaving.value = true;
      clearInterval(reloadCountdownInterval.value);
      await disconnectJitsi();
      window.location.replace(route('dashboard'));
    };

    return {
      sessionStore,
      activeTab,
      clinicianName,
      leaveSession,
      showNetworkChangeWarning,
      reloadCountdown,
    };
  },
};
</script>
